import { setLocalStorageItem } from 'businesslayer/minutesLocalStore'
import { transientStorageManager } from 'businesslayer/minutesSessionStore'
import {
    updatePregenerationAIMinutes,
    updateTranscriptAIMinutes
} from 'businesslayer/networkrequests'
import { updateWritingStyleAIMinutes } from 'businesslayer/networkrequests/AIStylesService'
import { getPreGenerationStatus } from 'businesslayer/networkrequests/preGenerationStatusService'
import i18n from 'common/util/internationalization'
import { useCommitteesState } from 'components/contexts/committee-context'
import {
    PREGENERATION_DOCUMENT_STATUS,
    WRITING_STYLE_STATUS
} from 'components/minutetaker/components/InvitationView/types'
import React, { useEffect, useRef } from 'react'
import { ERROR_MESSAGES_CONTENT_TOO_LARGE } from 'common/constants/constant'

interface ToastProp {
    id: number | string
    preGenPollingStatus: string
    minuteInitiateUser: string
    loggedInUser: string
    statusMessage?: string
    view_toast: boolean
    MinutesTitle?: string
    forTranscript: boolean
    forWritingStyle?: boolean
    errorType?: string | null
    writingStyleId?: string | number
}

export const useMinutesPolling = () => {
    const [toasts, setToasts] = React.useState<ToastProp[]>([])
    const [minutesDocuments, setMinutesDocuments] = React.useState([])
    const [transcriptDocuments, setTranscriptDocuments] = React.useState([])
    const [writingStyleDocuments, setWritingStyleDocuments] = React.useState([])
    const [isPolling, setIsPolling] = React.useState(true)
    const { adminCommittees } = useCommitteesState()
    const timeoutRefs = useRef<ReturnType<typeof setTimeout>[]>([]) // Store timeout IDs
    const toastTimeouts = useRef<Map<string | number, ReturnType<typeof setTimeout>>>(new Map())

    const getTranscriptDescription = (attributes) => {
        const { transcript_status, title, error_message } = attributes

        if (transcript_status === PREGENERATION_DOCUMENT_STATUS.UNAPPROVED_ERROR) {
            return i18n.t('UNAPPROVED_STATUS', { minutesTitle: title })
        }

        if (transcript_status === PREGENERATION_DOCUMENT_STATUS.AUTOMATED) {
            return i18n.t('AI_TTM_GENERATE_SUCCESS_SUBTITLE_DESCRIPTION', { minutesTitle: title })
        }

        if (transcript_status === PREGENERATION_DOCUMENT_STATUS.PARTIAL_SUCCESS) {
            return i18n.t('AI_TTM_GENERATE_PARTIAL_FAIL_SUBTITLE_DESCRIPTION', {
                minutesTitle: title
            })
        }

        if (transcript_status === PREGENERATION_DOCUMENT_STATUS.COMPLETE_MISMATCH) {
            return i18n.t('AI_TTM_GENERATE_FAIL_SUBTITLE_DESCRIPTION', { minutesTitle: title })
        }
        if (
            transcript_status === PREGENERATION_DOCUMENT_STATUS.FAILED &&
            error_message === ERROR_MESSAGES_CONTENT_TOO_LARGE
        ) {
            return i18n.t('AI_TTM_GENERATE_FAIL_LARGE_CONTENT', { minutesTitle: title })
        }

        return i18n.t('AI_TTM_GENERATE_FAIL', { minutesTitle: title })
    }

    const fetchMinutesDocuments = async (forceFetch: boolean = false) => {
        try {
            const committeeIdsArray = adminCommittees
                .map((item) => String(item.id))
                .filter((value, index, self) => self.indexOf(value) === index)
            setLocalStorageItem('committeeIds', JSON.stringify(committeeIdsArray))
            const response =
                isPolling === true || forceFetch === true
                    ? await getPreGenerationStatus(committeeIdsArray)
                    : null
            const pregenDocumentsData = response?.data
            const transcriptData = response?.transcript
            const writingStyleData = response?.writing_style || []
            setMinutesDocuments(pregenDocumentsData)
            setTranscriptDocuments(transcriptData)
            setWritingStyleDocuments(writingStyleData)

            const hasPregenProgress = pregenDocumentsData.some(
                (item: any) =>
                    item.attributes.pregenerated_minutes ===
                        PREGENERATION_DOCUMENT_STATUS.PROGRESS ||
                    item.attributes.pregenerated_minutes ===
                        PREGENERATION_DOCUMENT_STATUS.SUMMARY_IN_PROGRESS ||
                    item.attributes.pregenerated_minutes ===
                        PREGENERATION_DOCUMENT_STATUS.MINUTES_AI_IN_PROGRESS
            )
            const hasTranscriptProgress = transcriptData.some(
                (item: any) =>
                    item.attributes.transcript_status === PREGENERATION_DOCUMENT_STATUS.PROGRESS
            )
            const hasWritingStyleProgress = writingStyleData.some(
                (item: any) =>
                    item.attributes.writing_style_status === WRITING_STYLE_STATUS.PROGRESS
            )
            if (!(hasPregenProgress || hasTranscriptProgress || hasWritingStyleProgress)) {
                setIsPolling(false)
            }

            const newToasts = pregenDocumentsData
                .filter(
                    ({ attributes }) =>
                        attributes.pregenerated_minutes !==
                            PREGENERATION_DOCUMENT_STATUS.PROGRESS &&
                        attributes.pregenerated_minutes !==
                            PREGENERATION_DOCUMENT_STATUS.SUMMARY_IN_PROGRESS &&
                        attributes.pregenerated_minutes !==
                            PREGENERATION_DOCUMENT_STATUS.MINUTES_AI_IN_PROGRESS
                )
                .map(({ attributes }) => ({
                    id: `${attributes.document_id}`,
                    MinutesTitle:
                        attributes.pregenerated_minutes ===
                        PREGENERATION_DOCUMENT_STATUS.UNAPPROVED_ERROR
                            ? i18n.t('AI_GENERATE_FAIL_TITLE')
                            : attributes.pregenerated_minutes ===
                              PREGENERATION_DOCUMENT_STATUS.AUTOMATED
                            ? i18n.t('MEETING_MINUTES_READY')
                            : attributes.pregenerated_minutes ===
                              PREGENERATION_DOCUMENT_STATUS.PARTIAL_SUCCESS
                            ? i18n.t('AI_TTM_GENERATE_PARTIAL_FAIL_TITLE')
                            : attributes.pregenerated_minutes ===
                              PREGENERATION_DOCUMENT_STATUS.SUMMARY_FAILED
                            ? i18n.t('AI_SUMMARY_FAILED_TITLE')
                            : i18n.t('AI_GENERATE_FAIL_TITLE'),
                    minuteInitiateUser: `${attributes.author_id}`,
                    loggedInUser: `${transientStorageManager?.currentUser?.id}`,
                    preGenPollingStatus: `${attributes.pregenerated_minutes}`,
                    view_toast: attributes.view_toast,
                    statusMessage:
                        attributes.pregenerated_minutes ===
                        PREGENERATION_DOCUMENT_STATUS.UNAPPROVED_ERROR
                            ? i18n.t('UNAPPROVED_STATUS', {
                                  minutesTitle: attributes.title
                              })
                            : attributes.pregenerated_minutes ===
                              PREGENERATION_DOCUMENT_STATUS.AUTOMATED
                            ? i18n.t('AI_GENERATE_SUCCESS_DESCRIPTION', {
                                  minutesTitle: attributes.title
                              })
                            : attributes.pregenerated_minutes ===
                              PREGENERATION_DOCUMENT_STATUS.PARTIAL_SUCCESS
                            ? i18n.t('AI_GENERATE_PARTIAL_FAIL_SUBTITLE', {
                                  minutesTitle: attributes.title
                              })
                            : attributes.pregenerated_minutes ===
                              PREGENERATION_DOCUMENT_STATUS.SUMMARY_FAILED
                            ? i18n.t('AI_SUMMARY_FAILED_SUBTITLE', {
                                  minutesTitle: attributes.title
                              })
                            : i18n.t('AI_GENERATE_FAIL_SUBTITLE', {
                                  minutesTitle: attributes.title
                              }),
                    forTranscript: false
                }))

            const newTranscriptToasts = transcriptData
                .filter(
                    ({ attributes }) =>
                        attributes.transcript_status !== PREGENERATION_DOCUMENT_STATUS.PROGRESS
                )
                .map(({ attributes }) => ({
                    id: `${attributes.document_id}`,
                    MinutesTitle:
                        attributes.transcript_status ===
                        PREGENERATION_DOCUMENT_STATUS.UNAPPROVED_ERROR
                            ? i18n.t('AI_GENERATE_FAIL_TITLE')
                            : attributes.transcript_status ===
                              PREGENERATION_DOCUMENT_STATUS.AUTOMATED
                            ? i18n.t('MEETING_MINUTES_READY')
                            : attributes.transcript_status ===
                              PREGENERATION_DOCUMENT_STATUS.PARTIAL_SUCCESS
                            ? i18n.t('AI_TTM_GENERATE_PARTIAL_FAIL_TITLE')
                            : attributes.transcript_status ===
                                  PREGENERATION_DOCUMENT_STATUS.FAILED &&
                              attributes.error_message === ERROR_MESSAGES_CONTENT_TOO_LARGE
                            ? i18n.t('AI_TTM_FAILED_TO_GENERATE')
                            : i18n.t('FAILED_TO_GENERATE'),
                    minuteInitiateUser: `${attributes.author_id}`,
                    loggedInUser: `${transientStorageManager?.currentUser?.id}`,
                    preGenPollingStatus: `${attributes.transcript_status}`,
                    view_toast: attributes.view_toast,
                    statusMessage: getTranscriptDescription(attributes),
                    forTranscript: true,
                    forWritingStyle: false
                }))

            const newWritingStyleToasts = writingStyleData
                .filter(
                    ({ attributes }) =>
                        attributes.writing_style_status !== WRITING_STYLE_STATUS.PROGRESS &&
                        attributes.writing_style_status !== WRITING_STYLE_STATUS.ACKNOWLEDGED
                )
                .map(({ attributes, id }) => ({
                    id: `writing-style-${id}`,
                    writingStyleId: id,
                    preGenPollingStatus: `${attributes.writing_style_status}`,
                    minuteInitiateUser: `${attributes.author_id || ''}`,
                    loggedInUser: `${transientStorageManager?.currentUser?.id}`,
                    view_toast: attributes.view_toast !== false,
                    errorType: attributes.error_type || null,
                    forWritingStyle: true,
                    forTranscript: false
                }))

            setToasts((prevToasts) => {
                // Remove existing toasts with the same IDs to prevent duplicates
                const newToastIds = new Set([
                    ...newToasts.map((t) => t.id),
                    ...newTranscriptToasts.map((t) => t.id),
                    ...newWritingStyleToasts.map((t) => t.id)
                ])

                // Clear timeouts for toasts that are being replaced
                prevToasts.forEach((toast) => {
                    if (newToastIds.has(toast.id)) {
                        const existingTimeout = toastTimeouts.current.get(toast.id)
                        if (existingTimeout) {
                            clearTimeout(existingTimeout)
                            toastTimeouts.current.delete(toast.id)
                        }
                    }
                })

                const filteredPrevToasts = prevToasts.filter((t) => !newToastIds.has(t.id))
                return [
                    ...filteredPrevToasts,
                    ...newToasts,
                    ...newTranscriptToasts,
                    ...newWritingStyleToasts
                ]
            })
            ;[...newToasts, ...newTranscriptToasts, ...newWritingStyleToasts].forEach((toast) => {
                const timeoutId = setTimeout(() => {
                    setToasts((prevToasts) => prevToasts.filter((t) => t.id !== toast.id))
                    toastTimeouts.current.delete(toast.id)
                    if (toast.view_toast === true) {
                        if (toast.forTranscript) {
                            updateTranscriptAIMinutes(toast.id, toast.preGenPollingStatus, false)
                        } else if (toast.forWritingStyle && toast.writingStyleId) {
                            updateWritingStyleAIMinutes(toast.writingStyleId)
                        } else {
                            updatePregenerationAIMinutes(toast.id, toast.preGenPollingStatus, false)
                        }
                    }
                }, 15000)
                timeoutRefs.current.push(timeoutId) // Store timeout ID
                toastTimeouts.current.set(toast.id, timeoutId) // Track timeout by toast ID
            })
        } catch (error) {
            console.error('Error fetching minutes pregenDocumentsData:', error)
        }
    }

    useEffect(() => {
        // Capture current ref values for cleanup
        const currentTimeoutRefs = timeoutRefs.current
        const currentToastTimeouts = toastTimeouts.current

        return () => {
            // Cleanup on unmount
            currentTimeoutRefs.forEach((timeoutId) => clearTimeout(timeoutId))
            currentTimeoutRefs.length = 0
            currentToastTimeouts.forEach((timeoutId) => clearTimeout(timeoutId))
            currentToastTimeouts.clear()
        }
    }, [])

    return {
        isPolling,
        toasts,
        setToasts,
        minutesDocuments,
        fetchMinutesDocuments,
        transcriptDocuments,
        setIsPolling,
        writingStyleDocuments
    }
}
